import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { callable } from '../lib/callable';
import { KUNJEAN_CREST_URL } from '../lib/kunjeanCrest';

// Fixed-size "Kunjean Heroes" tribute panel for the home page — right column on desktop,
// full-width stacked card on mobile. Same 340×560 desktop box on BOTH shreyaadieducation.in
// and shreyaadi.co.in (that repo builds its own copy of this component against the same
// kunjeanStoriesFeed, so the two stay visually identical without sharing a local data copy).
// Deliberately restrained: one featured card at a time, no CTA beyond "See all →" — a
// tribute panel isn't a marketing widget. The featured story is picked at random from ALL
// published stories (both Gallantry and Achievers), and rotates to a new random pick every
// ROTATE_MS — a static pick would only ever surface whichever story happens to sort first,
// so a growing roster stays invisible for most visitors otherwise. This is the one
// auto-moving element in the section (everywhere else is explicitly static); kept slow and
// unannounced (no slide transition, no dots) so it reads as "comes back to a different one
// later," not a ticker.
const ROTATE_MS = 120_000;
//
// Photo: a 3:4 portrait box (object-fit: cover, object-position: top) so the whole
// face/uniform shows rather than a square crop trimming the crown/chin. photoUrl is always
// the ORIGINAL uploaded file's download URL (see AdminKunjeanStories' handlePhoto) — the
// storage-resize-images extension generates smaller webp variants alongside it, but nothing
// here references those, so display is never upscaled from a small thumbnail. If a photo
// still looks soft, the uploaded source itself is low-resolution — re-upload a larger one.
interface Story { slug: string; name: string; photoUrl: string | null; decorations: string[]; brief: string }
interface FeedResponse { stories: Story[] }

function pickRandom(stories: Story[], excludeSlug: string | undefined): Story | null {
  if (stories.length === 0) return null;
  // Avoid re-picking the same story back-to-back when there's more than one to choose from —
  // otherwise "rotates every 2 minutes" would visibly do nothing about half the time.
  const pool = stories.length > 1 ? stories.filter((s) => s.slug !== excludeSlug) : stories;
  return pool[Math.floor(Math.random() * pool.length)]!;
}

export function KunjeanHeroPanel() {
  const [stories, setStories] = useState<Story[]>([]);
  const [story, setStory] = useState<Story | null>(null);

  useEffect(() => {
    callable<unknown, FeedResponse>('listKunjeanStories', {})
      .then((r) => {
        setStories(r.data.stories);
        setStory(pickRandom(r.data.stories, undefined));
      })
      .catch(() => setStories([]));
  }, []);

  useEffect(() => {
    if (stories.length < 2) return;
    const id = setInterval(() => setStory((current) => pickRandom(stories, current?.slug)), ROTATE_MS);
    return () => clearInterval(id);
  }, [stories]);

  if (!story) return null;

  return (
    <aside className="kj-panel">
      <style>{`
        .kj-panel {
          width: 100%;
          box-sizing: border-box;
          background: #faf8f4;
          border: 1px solid #e8e2d3;
          border-radius: 12px;
          padding: 0.9rem;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .kj-panel-photo { width: 100%; aspect-ratio: 3 / 4; object-fit: cover; object-position: top center; border-radius: 10px; flex-shrink: 0; display: block; }
        @media (min-width: 900px) {
          .kj-panel { width: 340px; height: 560px; position: sticky; top: 96px; flex-shrink: 0; }
          /* The fixed 560px box can't fit a full-bleed-width 3:4 photo plus name/medals/brief/
             link — do the arithmetic and it overflows by ~35px. Capping height (width auto via
             aspect-ratio) instead of deriving height from 100% width keeps the exact 3:4 crop
             and fits everything with room to spare. Mobile has no such ceiling, so it keeps the
             full-width version above for a proper full-bleed portrait there. */
          .kj-panel-photo { width: auto; max-width: 100%; height: 340px; margin: 0 auto; }
        }
        .kj-panel-brief {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
      <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#5c4a2a', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.4rem' }}><img src={KUNJEAN_CREST_URL} alt="" width={18} height={20} /> Kunjean Heroes</div>
      <Link to={`/kunjeans/${story.slug}`} style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, textDecoration: 'none', color: 'inherit' }}>
        {story.photoUrl
          ? <img src={story.photoUrl} alt={story.name} loading="lazy" className="kj-panel-photo" />
          : <div className="kj-panel-photo" style={{ background: '#e8e2d3', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}><img src={KUNJEAN_CREST_URL} alt="" style={{ maxWidth: '70%', maxHeight: '70%' }} /></div>}
        <div style={{ marginTop: '0.5rem', fontSize: '1rem', fontWeight: 700, color: '#3a3324', flexShrink: 0 }}>{story.name}</div>
        {story.decorations.length > 0 && <div style={{ marginTop: '0.2rem', fontSize: '0.8rem', color: '#8a6d1f', fontWeight: 600, flexShrink: 0 }}>{story.decorations.map((d) => `🏅 ${d}`).join(' · ')}</div>}
        <p className="kj-panel-brief" style={{ marginTop: '0.35rem', fontSize: '0.82rem', color: '#4a4438', lineHeight: 1.4, flexShrink: 0 }}>{story.brief}</p>
      </Link>
      <Link to="/kunjeans" style={{ marginTop: '0.4rem', fontSize: '0.82rem', fontWeight: 600, color: '#5c4a2a', textDecoration: 'none', flexShrink: 0 }}>See all →</Link>
    </aside>
  );
}
